import React from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import styles from './Notifications.module.scss';
import { Notification } from '../types/notification.types';
import { fetchUserNotifications, markNotificationAsRead } from '../api/notificationsApi';
import { mapAPINotificationsToNotifications } from '../utils/notificationMappers';

interface NotificationsProps {
	isOpen: boolean;
	onClose: () => void;
}

const SLIDE_OUT_MS = 280;
const COLLAPSE_MS = 200;
const CLEAR_ANIMATION_MS = SLIDE_OUT_MS + COLLAPSE_MS;
const CLEAR_ALL_STAGGER_MS = 45;

const Notifications: React.FC<NotificationsProps> = ({ isOpen, onClose }) => {
	const history = useHistory();
	const queryClient = useQueryClient();
	const [markingAsRead, setMarkingAsRead] = React.useState<string | null>(null);
	const [markingAllAsRead, setMarkingAllAsRead] = React.useState(false);
	const [removingNotifications, setRemovingNotifications] = React.useState<Set<string>>(new Set());
	const [clearAllStarted, setClearAllStarted] = React.useState(false);

	// Fetch notifications from API
	const { data: apiNotifications = [], isLoading } = useQuery(
		'userNotifications',
		fetchUserNotifications,
		{
			refetchInterval: 30000, // Refetch every 30 seconds
			refetchIntervalInBackground: false,
			staleTime: 10000,
		}
	);

	const notifications = mapAPINotificationsToNotifications(apiNotifications);
	// Keep clearing items in the list so the exit animation can play
	const displayedNotifications = notifications.filter((n) => !n.isRead);
	const unreadCount = displayedNotifications.filter(
		(n) => !removingNotifications.has(n.id)
	).length;

	const finishRemoval = (ids: string[], animationStartedAt: number, durationMs = CLEAR_ANIMATION_MS) => {
		const remaining = Math.max(0, durationMs - (Date.now() - animationStartedAt));
		setTimeout(() => {
			queryClient.invalidateQueries('userNotifications');
			setRemovingNotifications((prev) => {
				const next = new Set(prev);
				ids.forEach((id) => next.delete(id));
				return next;
			});
			setClearAllStarted(false);
		}, remaining);
	};

	const markAsRead = async (id: string) => {
		if (removingNotifications.has(id)) return;

		setMarkingAsRead(id);
		setRemovingNotifications((prev) => new Set(prev).add(id));
		const animationStartedAt = Date.now();

		try {
			await markNotificationAsRead(id);
			setMarkingAsRead(null);
			finishRemoval([id], animationStartedAt);
		} catch (error) {
			setRemovingNotifications((prev) => {
				const next = new Set(prev);
				next.delete(id);
				return next;
			});
			setMarkingAsRead(null);
		}
	};

	const markAllAsRead = async () => {
		const unreadNotifications = displayedNotifications.filter(
			(n) => !removingNotifications.has(n.id)
		);
		if (unreadNotifications.length === 0) return;

		setMarkingAllAsRead(true);
		setClearAllStarted(true);
		const ids = unreadNotifications.map((notification) => notification.id);
		setRemovingNotifications(new Set(ids));
		const animationStartedAt = Date.now();
		const totalDuration =
			CLEAR_ANIMATION_MS + Math.max(0, ids.length - 1) * CLEAR_ALL_STAGGER_MS;

		try {
			await Promise.all(ids.map((id) => markNotificationAsRead(id)));

			const remaining = Math.max(0, totalDuration - (Date.now() - animationStartedAt));
			setTimeout(() => {
				queryClient.invalidateQueries('userNotifications');
				setMarkingAllAsRead(false);
				setRemovingNotifications(new Set());
				setClearAllStarted(false);
				onClose();
			}, remaining);
		} catch (error) {
			setRemovingNotifications(new Set());
			setMarkingAllAsRead(false);
			setClearAllStarted(false);
		}
	};

	const handleNotificationClick = (notification: Notification) => {
		if (!notification.isRead) {
			markAsRead(notification.id);
		}
		if (notification.actionUrl) {
			history.push(notification.actionUrl);
			onClose();
		}
	};

	const formatTimestamp = (timestamp: Date): string => {
		const now = new Date();
		const diff = now.getTime() - timestamp.getTime();
		const minutes = Math.floor(diff / (1000 * 60));
		const hours = Math.floor(diff / (1000 * 60 * 60));
		const days = Math.floor(diff / (1000 * 60 * 60 * 24));

		if (minutes < 1) return 'Just now';
		if (minutes < 60) return `${minutes}m ago`;
		if (hours < 24) return `${hours}h ago`;
		if (days === 1) return 'Yesterday';
		if (days < 7) return `${days}d ago`;
		return timestamp.toLocaleDateString();
	};

	if (!isOpen) return null;

	return (
		<>
			<div className={styles.notificationsOverlay} onClick={onClose} />
			<div className={styles.notificationsContainer}>
				<div className={styles.notificationsHeader}>
					<div className={styles.headerLeft}>
						<h3>Notifications</h3>
						{unreadCount > 0 && <span className={styles.unreadBadge}>{unreadCount}</span>}
					</div>
					{unreadCount > 0 && (
						<button
							className={styles.markAllButton}
							onClick={markAllAsRead}
							disabled={markingAllAsRead}
						>
							{markingAllAsRead ? (
								<span className={styles.buttonContent}>
									<span className={styles.spinner} />
									Clearing...
								</span>
							) : (
								'Clear all'
							)}
						</button>
					)}
				</div>

				<div className={styles.notificationsList}>
					{isLoading ? (
						<div className={styles.emptyState}>
							<p>Loading notifications...</p>
						</div>
					) : displayedNotifications.length === 0 ? (
						<div className={styles.emptyState}>
							<p>No notifications</p>
						</div>
					) : (
						displayedNotifications.map((notification, index) => {
								const isRemoving = removingNotifications.has(notification.id);
								const staggerMs = clearAllStarted ? index * CLEAR_ALL_STAGGER_MS : 0;

								return (
								<div
									key={notification.id}
									className={`${styles.notificationSlot} ${isRemoving ? styles.slotCollapsing : ''}`}
									style={
										isRemoving
											? { transitionDelay: `${SLIDE_OUT_MS + staggerMs}ms` }
											: undefined
									}
								>
									<div className={styles.notificationSlotInner}>
										<div
											className={`${styles.notificationItem} ${styles.unread} ${styles[notification.type]} ${
												isRemoving ? styles.removing : ''
											}`}
											style={
												isRemoving && staggerMs > 0
													? { animationDelay: `${staggerMs}ms` }
													: undefined
											}
											onClick={() => !isRemoving && handleNotificationClick(notification)}
										>
											<div className={styles.notificationContent}>
												<div className={styles.notificationHeader}>
													<h4>{notification.title}</h4>
													<span className={styles.timestamp}>
														{formatTimestamp(notification.timestamp)}
													</span>
												</div>
												<p className={styles.notificationMessage}>{notification.message}</p>
											</div>
											<button
												className={styles.markReadButton}
												onClick={(e) => {
													e.stopPropagation();
													markAsRead(notification.id);
												}}
												disabled={isRemoving || markingAsRead === notification.id}
											>
												Clear
											</button>
										</div>
									</div>
								</div>
								);
							})
					)}
				</div>
			</div>
		</>
	);
};

export default Notifications;
